import { newEventId } from "./eventId";
import { initGtm } from "./gtmBootstrap";

const FIRED_EVENTS_KEY = "lawfusion_fired_conversion_events";

const hasFired = (key) => {
  try {
    const raw = localStorage.getItem(FIRED_EVENTS_KEY);
    if (!raw) return false;
    const set = JSON.parse(raw);
    return Array.isArray(set) && set.includes(key);
  } catch {
    return false;
  }
};

const markFired = (key) => {
  try {
    const raw = localStorage.getItem(FIRED_EVENTS_KEY);
    const set = raw ? JSON.parse(raw) : [];
    const next = Array.isArray(set) ? set : [];
    if (!next.includes(key)) next.push(key);
    const trimmed = next.slice(-50);
    localStorage.setItem(FIRED_EVENTS_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage unavailable — best-effort only.
  }
};

const push = (payload) => {
  if (typeof window === "undefined") return;
  initGtm();
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

// SHA-256 hex (lowercase, trimmed) — required format for Reddit/Meta user_data.
const sha256Hex = async (value) => {
  if (value == null || value === "") return undefined;
  if (typeof crypto === "undefined" || !crypto.subtle) return undefined;
  const buf = new TextEncoder().encode(String(value).trim().toLowerCase());
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

const normalizePhone = (phone) => {
  if (!phone) return undefined;
  const digits = String(phone).replace(/\D/g, "");
  return digits || undefined;
};

/** Normalize AuthContext user, API analytics.user, or localStorage shapes. */
export const normalizeUserForAnalytics = (user) => {
  if (!user) return undefined;

  const userId = user.userId || user._id || user.id || user.stripeCustomerId;
  const email = user.email;
  const phone = normalizePhone(user.phone);
  const firstName = user.firstName || user.first_name || null;
  const lastName = user.lastName || user.last_name || null;
  const city = user.city || null;
  const state = user.state || null;
  const country = user.country || null;
  const zip = user.zip || user.postalCode || user.postal_code || null;

  if (
    !email &&
    !phone &&
    !userId &&
    !firstName &&
    !lastName &&
    !city &&
    !state &&
    !country &&
    !zip
  ) {
    return undefined;
  }

  return {
    email,
    phone,
    userId,
    stripeCustomerId: user.stripeCustomerId,
    firstName,
    lastName,
    city,
    state,
    country,
    zip,
  };
};

/** Meta / Reddit CAPI-style hashed user_data. */
const buildUserData = async (user) => {
  const normalized = normalizeUserForAnalytics(user);
  if (!normalized) return undefined;

  const data = {};
  if (normalized.email) data.em = await sha256Hex(normalized.email);
  if (normalized.phone) data.ph = await sha256Hex(normalized.phone);
  if (normalized.firstName) data.fn = await sha256Hex(normalized.firstName);
  if (normalized.lastName) data.ln = await sha256Hex(normalized.lastName);
  if (normalized.city) data.ct = await sha256Hex(normalized.city);
  if (normalized.state) data.st = await sha256Hex(normalized.state);
  if (normalized.country) data.country = await sha256Hex(normalized.country);
  if (normalized.zip) data.zp = await sha256Hex(normalized.zip);
  if (normalized.userId || normalized.stripeCustomerId) {
    data.external_id = await sha256Hex(
      normalized.userId || normalized.stripeCustomerId,
    );
  }

  return Object.keys(data).length ? data : undefined;
};

// Fires a page_view to dataLayer on every route change. Reddit's "Page Visit"
// match-quality score depends on this firing with hashed user_data when the
// user is logged in.
export const trackPageView = async ({ user, path } = {}) => {
  const event_id = newEventId();
  const user_data = await buildUserData(user);
  push({
    event: "page_view",
    event_id,
    page_path: path || (typeof window !== "undefined" ? window.location.pathname : undefined),
    page_location: typeof window !== "undefined" ? window.location.href : undefined,
    user_data,
  });
  return event_id;
};

// Fires on successful registration. Pass `method: "google" | "email"`.
export const trackSignUp = async ({ user, method, eventId } = {}) => {
  const event_id = eventId || newEventId();
  const user_data = await buildUserData(user);
  push({
    event: "sign_up",
    event_id,
    method: method || "email",
    user_data,
  });
  return event_id;
};

// High-intent step before checkout (plan selected → Stripe). Maps to Reddit
// "Add To Wishlist" and Meta add_to_wishlist in the server GTM container.
export const trackAddToWishlist = async ({ user, item, eventId } = {}) => {
  const event_id = eventId || newEventId();
  const user_data = await buildUserData(user);
  push({
    event: "add_to_wishlist",
    event_id,
    user_data,
    ecommerce: item
      ? {
          currency: "USD",
          value: item.price || 0,
          items: [item],
        }
      : undefined,
  });
  return event_id;
};

const fireSubscribeEvent = async ({
  dedupeKey,
  eventId,
  user,
  analytics,
}) => {
  if (!analytics?.transactionId) return null;
  if (hasFired(dedupeKey)) return null;

  const event_id = eventId || analytics.eventId || newEventId();
  const user_data = await buildUserData(user);
  const currency = (analytics.currency || "usd").toUpperCase();
  const value =
    typeof analytics.value === "number"
      ? analytics.value
      : typeof analytics.planAmount === "number"
        ? analytics.planAmount
        : analytics.chargedAmount || 0;

  const item = {
    item_id: analytics.priceId || analytics.planKey || "subscription",
    item_name: analytics.planName || "subscription",
    item_category: analytics.planType || "subscription",
    price: value,
    quantity: 1,
  };

  const ecommerce = {
    transaction_id: analytics.transactionId,
    value,
    currency,
    payment_method: analytics.paymentMethod || null,
    plan_name: analytics.planName || null,
    plan_type: analytics.planType || "subscription",
    billing_cycle: analytics.billingCycle || (analytics.isTrial ? "trial" : "monthly"),
    items: [item],
    subscription_id: analytics.subscriptionId || null,
    is_trial: !!analytics.isTrial,
    amount_charged_now:
      typeof analytics.chargedAmount === "number" ? analytics.chargedAmount : 0,
  };

  push({ ecommerce: null });
  push({
    event: "subscribe",
    event_id,
    user_data,
    transaction_id: analytics.transactionId,
    value,
    currency,
    payment_method: ecommerce.payment_method,
    plan_name: ecommerce.plan_name,
    plan_type: ecommerce.plan_type,
    billing_cycle: ecommerce.billing_cycle,
    ecommerce,
  });

  markFired(dedupeKey);
  return event_id;
};

/**
 * Fires the subscribe event to GTM after Stripe subscription checkout.
 * Deduped by Stripe session id. Shares event_id with server-side CAPI.
 */
export const trackSubscriptionConversion = async (analytics, user, eventId) => {
  if (!analytics?.transactionId) return null;

  const dedupeKey = `subscribe:${analytics.transactionId}`;
  const resolvedUser = normalizeUserForAnalytics(user) || analytics.user;

  return fireSubscribeEvent({
    dedupeKey,
    eventId,
    user: resolvedUser,
    analytics,
  });
};

/** One-time Stripe checkout (e.g. token top-up). Always fires `purchase`. */
export const trackPurchase = async (analytics, user, eventId) => {
  if (!analytics?.transactionId) return null;

  const dedupeKey = `purchase:${analytics.transactionId}`;
  if (hasFired(dedupeKey)) return null;

  const value =
    typeof analytics.chargedAmount === "number" ? analytics.chargedAmount : 0;
  const event_id = eventId || analytics.eventId || newEventId();
  const resolvedUser = normalizeUserForAnalytics(user) || analytics.user;
  const user_data = await buildUserData(resolvedUser);

  const item = {
    item_id: analytics.itemCategory || "one_time",
    item_name: analytics.itemName || "purchase",
    item_category: analytics.itemCategory || "one_time",
    price: value,
    quantity: 1,
  };

  push({ ecommerce: null });
  push({
    event: "purchase",
    event_id,
    user_data,
    transaction_id: analytics.transactionId,
    value,
    currency: (analytics.currency || "usd").toUpperCase(),
    ecommerce: {
      transaction_id: analytics.transactionId,
      value,
      currency: (analytics.currency || "usd").toUpperCase(),
      items: [item],
    },
  });

  markFired(dedupeKey);
  return event_id;
};
